import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import { anchorsToSvgPath, parseSvgPathToAnchors } from '@/lib/elements/pen-path-utils'
import { scalePathCommands } from '../helpers/shape-list'

/**
 * The committed shape's path, rescaled to the resized box — `pathCommands` and their SVG path, or a
 * pen path's anchors — mirroring what `applyPartialShapeUpdate` does to the live shape.
 */
export function rescaleCommittedPath(
  baseShape: ShapeData,
  next: { width: number; height: number },
  committedShapeIn: ShapeData
): ShapeData {
  let committedShape = committedShapeIn

  const hasCommands =
    Array.isArray(baseShape.pathCommands) && (baseShape.pathCommands?.length ?? 0) > 0
  const sizeChanged = next.width !== baseShape.width || next.height !== baseShape.height

  if (hasCommands && sizeChanged) {
    const oldW = baseShape.width
    const oldH = baseShape.height
    const newW = next.width
    const newH = next.height

    if (
      oldW > 0 &&
      oldH > 0 &&
      newW > 0 &&
      newH > 0 &&
      Number.isFinite(oldW) &&
      Number.isFinite(oldH) &&
      Number.isFinite(newW) &&
      Number.isFinite(newH)
    ) {
      const sx = newW / oldW
      const sy = newH / oldH

      if (Number.isFinite(sx) && Number.isFinite(sy) && (sx !== 1 || sy !== 1)) {
        const scaled = scalePathCommands(baseShape.pathCommands as PathCommand[], sx, sy)
        const close = baseShape.type !== 'path' || /Z\s*$/i.test((baseShape.path ?? '').trim())

        committedShape = {
          ...committedShape,
          pathCommands: scaled,
          path: pathCommandsToSvgPath(scaled, { close }),
        }
      }
    }
  }

  const isPenPath =
    !hasCommands && typeof baseShape.path === 'string' && baseShape.path.trim().length > 0

  if (isPenPath && sizeChanged) {
    const oldW = baseShape.width
    const oldH = baseShape.height
    const newW = next.width
    const newH = next.height

    if (
      oldW > 0 &&
      oldH > 0 &&
      newW > 0 &&
      newH > 0 &&
      Number.isFinite(oldW) &&
      Number.isFinite(oldH) &&
      Number.isFinite(newW) &&
      Number.isFinite(newH)
    ) {
      const sx = newW / oldW
      const sy = newH / oldH

      if (Number.isFinite(sx) && Number.isFinite(sy) && (sx !== 1 || sy !== 1)) {
        const { points, subpaths } = parseSvgPathToAnchors(baseShape.path!)
        const isClosed = /Z\s*$/i.test(baseShape.path!.trim())
        const scaledPoints = points.map((pt) => ({
          ...pt,
          x: pt.x * sx,
          y: pt.y * sy,
          handleIn: pt.handleIn ? { x: pt.handleIn.x * sx, y: pt.handleIn.y * sy } : null,
          handleOut: pt.handleOut ? { x: pt.handleOut.x * sx, y: pt.handleOut.y * sy } : null,
        }))
        const scaledSubpaths = subpaths.map((sp, idx) =>
          idx === subpaths.length - 1 ? { ...sp, isClosed } : sp
        )

        committedShape = {
          ...committedShape,
          path: anchorsToSvgPath(scaledPoints, isClosed, scaledSubpaths),
        }
      }
    }
  }

  return committedShape
}
